import { useState } from 'react';
import { Tag, Check, Loader2, X } from 'lucide-react';
import { apiFetchJson } from '../lib/api';
import { useCartStore } from '../store/cartStore';
import { formatPrice } from '../lib/utils';
import type { GiftCodeValidation } from '../types';

interface PromoCodeInputProps {
  orderAmount: number;
  onApplied?: (promo: GiftCodeValidation | null) => void;
}

export default function PromoCodeInput({ orderAmount, onApplied }: PromoCodeInputProps) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const giftCode = useCartStore((s) => s.giftCode);
  const setGiftCode = useCartStore((s) => s.setGiftCode);

  const handleApply = async () => {
    if (!code.trim()) return;

    setLoading(true);
    setError('');

    const res = await apiFetchJson<GiftCodeValidation>('/public/gift-codes/validate', {
      method: 'POST',
      body: JSON.stringify({ code: code.trim(), orderAmount }),
    });

    setLoading(false);

    if (res.success && res.data) {
      setGiftCode(res.data);
      onApplied?.(res.data);
      setCode('');
    } else {
      setError(res.error || '優惠碼無效或不符合使用條件');
      setGiftCode(null);
      onApplied?.(null);
    }
  };

  const handleRemove = () => {
    setGiftCode(null);
    onApplied?.(null);
    setError('');
  };

  if (giftCode) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Check className="text-green-600" size={20} />
          <div>
            <p className="font-bold text-green-800">{giftCode.code}</p>
            <p className="text-sm text-green-700">
              已減 {formatPrice(giftCode.discountAmount)}
              {giftCode.description ? ` · ${giftCode.description}` : ''}
            </p>
          </div>
        </div>
        <button onClick={handleRemove} className="text-stone-400 hover:text-red-500 p-1">
          <X size={20} />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-bold text-stone-700 flex items-center gap-2">
        <Tag size={16} className="text-x1493-secondary" />
        優惠碼
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApply(); } }}
          placeholder="輸入優惠碼"
          className="flex-1 p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-x1493-primary focus:border-x1493-primary outline-none"
        />
        <button
          type="button"
          onClick={handleApply}
          disabled={loading || !code.trim()}
          className="bg-x1493-dark text-white px-4 py-2 rounded-xl font-bold hover:bg-black disabled:bg-stone-300 transition flex items-center gap-2"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : '套用'}
        </button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
